import { MJCFParser } from "../../core/mjcfParser";
import type { MJCFParseResult } from "../../core/mjcfParser";
import type { Body, Material } from "../../types/scene";
import { getHistoryActions } from "../historyStore";
import { usePanelStore } from "../panelStore";
import { getSceneActions, getSceneState } from "../sceneStore";
import { useSelectionStore } from "../selectionStore";
import type { FileStoreActions, FileStoreGet, FileStoreSet } from "./types";
import { shouldBlockSceneAuthoringMutation } from "../../utils/physicsGuards";
import { requestSceneReplacementConfirmation } from "../../utils/sceneReplacementGuards";
import { buildProjectSaveSnapshotHash } from "../../utils/projectSaveSnapshot";
import { rememberDiscardedSceneForRestore } from "../../utils/discardedSceneRecovery";
import { getProjectSaveActions } from "../projectSaveStore";

export const createProjectActions = ({
  set,
  get,
}: {
  set: FileStoreSet;
  get: FileStoreGet;
}): Pick<FileStoreActions, "createNewProject" | "setProjectName" | "isCurrentProjectNew"> => ({
  createNewProject: async (
    projectName: string = "Untitled Scene",
    createEmptyXml: boolean = true
  ) => {
    if (shouldBlockSceneAuthoringMutation("createNewProject")) return;
    const state = get();
    const hasFileStoreWork = Boolean(
      state.xmlFile ||
      Object.keys(state.fileMap || {}).length > 0 ||
      Object.keys(state.keyframes || {}).length > 0 ||
      state.currentLoadingFolder
    );
    if (
      !(await requestSceneReplacementConfirmation({
        hasFileStoreWork,
        currentSnapshotHash: buildProjectSaveSnapshotHash({
          scene: getSceneState(),
          fileState: state,
        }),
      }))
    )
      return;
    const sceneActions = getSceneActions();
    const selectionActions = useSelectionStore.getState();
    const panelActions = usePanelStore.getState().actions;
    const historyActions = getHistoryActions();

    rememberDiscardedSceneForRestore();
    historyActions.clearHistory();
    selectionActions.clearAllSelections();
    selectionActions.clearAllHovers();
    panelActions.setExpandedBodies({});
    sceneActions.resetScene();
    sceneActions.clearJoints();

    get().actions.clearAllKeyframes();

    if (createEmptyXml) {
      const emptyXml = `<?xml version="1.0" encoding="UTF-8"?>
<mujoco model="${projectName.replace(/\s+/g, "_").toLowerCase()}">
	<compiler angle="radian" meshdir="assets"/>
	
	<option integrator="implicitfast"/>
	
	<default>
		<default class="main">
			<joint axis="0 0 1" range="-3.14159 3.14159"/>
			<geom type="mesh" contype="0" conaffinity="0" group="2"/>
			<default class="visual">
				<geom type="mesh" contype="0" conaffinity="0" group="2"/>
			</default>
			<default class="collision">
				<geom group="3"/>
			</default>
		</default>
	</default>
	
	<asset>
		<material name="default_mat" rgba="0.75 0.75 0.75 1" metallic="0.9" roughness="0.2"/>
	</asset>
	
	<worldbody>
		<light name="spotlight" pos="0 0 2" dir="0 0 -1"/>
		<body name="base" pos="0 0 0" childclass="main">
			<freejoint/>
			<inertial mass="1.0" pos="0 0 0" diaginertia="0.1 0.1 0.1"/>
		</body>
	</worldbody>
	
	<actuator>
	</actuator>
	
	<keyframe>
	</keyframe>
</mujoco>`;

      const xmlBlob = new Blob([emptyXml], { type: "application/xml" });
      const xmlFile = new File([xmlBlob], `${projectName.replace(/\s+/g, "_").toLowerCase()}.xml`, {
        type: "application/xml",
      });

      set({
        xmlFile,
        fileMap: {},
        referencedFiles: [],
        missingFiles: [],
        keyframes: {},
        keyframeDetails: {},
        preservedMjcfSections: {},
        pendingMjcfImportWarnings: null,
        projectName,
        isNewProject: true,
      });

      sceneActions.resetScene();

      const parser = new MJCFParser();
      const parsedResult = (await parser.parseFile(xmlFile)) as MJCFParseResult;
      const {
        materials: parsedMaterials,
        joints: parsedJoints,
        bodies: parsedBodies,
        worldBodies: parsedWorldBodies,
        sites: parsedSites,
        option: parsedOption,
        mjcfCompiler: parsedMjcfCompiler,
        mjcfStatistic: parsedMjcfStatistic,
        referencedFiles: parsedReferencedFiles,
        keyframes: parsedKeyframes,
        keyframeDetails: parsedKeyframeDetails,
        preservedMjcfSections: parsedPreservedMjcfSections,
      } = parsedResult;

      set({
        referencedFiles: parsedReferencedFiles,
        keyframes: parsedKeyframes,
        keyframeDetails: parsedKeyframeDetails || {},
        preservedMjcfSections: parsedPreservedMjcfSections || {},
      });

      sceneActions.loadMaterials(parsedMaterials as Record<string, Material>);
      if (parsedOption) sceneActions.updateOption(parsedOption);
      if (parsedMjcfCompiler) sceneActions.updateMjcfCompiler(parsedMjcfCompiler);
      if (parsedMjcfStatistic) sceneActions.updateMjcfStatistic(parsedMjcfStatistic);
      sceneActions.setBodies(parsedBodies as unknown as Record<string, Body>, parsedWorldBodies);
      if (parsedJoints) sceneActions.setJoints(parsedJoints);
      if (parsedSites) sceneActions.setSites(parsedSites);

      historyActions.clearHistory();
    } else {
      set({
        xmlFile: null,
        fileMap: {},
        referencedFiles: [],
        missingFiles: [],
        keyframes: {},
        keyframeDetails: {},
        preservedMjcfSections: {},
        pendingMjcfImportWarnings: null,
        projectName,
        isNewProject: true,
      });
    }
    getProjectSaveActions().resetProjectSaveState();
  },

  setProjectName: (name: string) => {
    if (shouldBlockSceneAuthoringMutation("setProjectName")) return;
    set({ projectName: name });
  },

  isCurrentProjectNew: (): boolean => {
    const state = get();
    return state.isNewProject || (!state.xmlFile && Object.keys(state.fileMap).length === 0);
  },
});
